<template>
  <div class="account-manage-container">
    <el-row :gutter="20">
      <el-col :span="3">
        <el-card class="tree-card">
          <ul class="account-type-list">
            <li v-for="(item, index) in accountTypes" :key="index" class="account-type-item"
              :class="{ 'active': conditions.acctType === item.statusCd }" @click="switchAccountType(item)">
              {{ item.name }}
            </li>
          </ul>
        </el-card>
      </el-col>

      <el-col :span="21">
        <el-card class="search-card">
          <div slot="header" class="text-left">
            <span>{{ $t('accountManage.searchTitle') }}</span>
          </div>

          <el-row :gutter="20">
            <el-col :span="6">
              <el-input v-model="conditions.acctName" :placeholder="$t('accountManage.placeholderName')" clearable />
            </el-col>

            <el-col :span="6">
              <el-input v-model="conditions.idCard" :placeholder="$t('accountManage.placeholderIdCard')" clearable />
            </el-col>

            <el-col :span="6">
              <el-input v-model="conditions.link" :placeholder="$t('accountManage.placeholderLink')" clearable />
            </el-col>

            <el-col :span="6">
              <el-button type="primary" @click="queryAccounts">
                {{ $t('accountManage.query') }}
              </el-button>
              <el-button @click="resetConditions">
                {{ $t('accountManage.reset') }}
              </el-button>
            </el-col>
          </el-row>
        </el-card>

        <el-card class="mt-20">
          <div slot="header" class="flex-between">
            <span>{{ $t('accountManage.ownerAccount') }}</span>
            <div>
              <el-button type="primary" size="small" icon="el-icon-plus" @click="openPrestore">
                {{ $t('accountManage.prestore') }}
              </el-button>
              <el-button type="warning" size="small" icon="el-icon-download" :loading="exporting" @click="exportAccounts">
                {{ $t('accountManage.exportPrestore') }}
              </el-button>
              <el-button type="success" size="small" icon="el-icon-upload2" @click="openImport">
                {{ $t('accountManage.importPrestore') }}
              </el-button>
            </div>
          </div>

          <el-table :data="accounts" border v-loading="loading">
            <el-table-column prop="acctId" :label="$t('accountManage.accountId')" align="center" />

            <el-table-column prop="acctName" :label="$t('accountManage.accountName')" align="center" />

            <el-table-column prop="idCard" :label="$t('accountManage.idCard')" align="center" />

            <el-table-column prop="link" :label="$t('accountManage.phone')" align="center" />

            <el-table-column prop="acctTypeName" :label="$t('accountManage.accountType')" align="center" />

            <el-table-column prop="amount" :label="$t('accountManage.accountAmount')" align="center" />

            <el-table-column prop="roomName" :label="$t('accountManage.deductionRoom')" align="center">
              <template slot-scope="scope">
                {{ scope.row.roomName || $t('common.none') }}
              </template>
            </el-table-column>

            <el-table-column prop="createTime" :label="$t('accountManage.createTime')" align="center" />

            <el-table-column :label="$t('accountManage.operations')" align="center" width="200">
              <template slot-scope="scope">
                <el-button size="mini" @click="viewAccountDetail(scope.row)">
                  {{ $t('accountManage.accountDetail') }}
                </el-button>
                <el-button size="mini" type="danger" @click="openDeleteDialog(scope.row)">
                  {{ $t('accountManage.delete') }}
                </el-button>
              </template>
            </el-table-column>
          </el-table>

          <el-pagination class="mt-20" :current-page.sync="pagination.current" :page-sizes="[10, 20, 30, 50]"
            :page-size="pagination.size" :total="pagination.total" layout="total, sizes, prev, pager, next, jumper"
            @size-change="handleSizeChange" @current-change="handlePageChange" />
        </el-card>
      </el-col>
    </el-row>

    <delete-account ref="deleteDialog" @success="fetchAccounts" />
    <prestore-account ref="prestoreAccount" @success="fetchAccounts" />
    <excel-import-mapper
      ref="prestoreImportMapper"
      hide-trigger
      :title="'覆盖导入' + (importAcctTypeName || '账户') + '余额'"
      :fields="importFields"
      :submit-handler="submitPrestoreImport"
      :template-download-handler="downloadTemplate"
      :template-download-text="$t('accountManage.downloadTemplate')"
      :show-default-value-column="false"
      :show-value-mapping-column="false"
      :show-source-column-select="false"
      :show-field-description="true"
      :show-source-data="false"
      output-sheet-name="预存数据"
      output-file-name="预存导入-已匹配.xlsx"
      :max-file-size-mb="10"
      :max-data-rows="5000"
      :auto-close="true"
      :show-success-message="false"
      @success="handleImportSuccess"
      @error="handleImportError"
    />
    <el-dialog :title="$t('accountManage.importFailed')" :visible.sync="importErrorVisible" width="640px">
      <div style="margin-bottom: 12px">{{ importErrorSummary }}</div>
      <el-table :data="importErrors" border max-height="360">
        <el-table-column prop="rowIndex" label="行号" width="80" align="center" />
        <el-table-column prop="ownerName" label="业主名称" width="140" />
        <el-table-column prop="ownerTel" label="手机号" width="130" />
        <el-table-column prop="message" label="原因" />
      </el-table>
      <span slot="footer">
        <el-button type="primary" @click="importErrorVisible = false">关闭</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { queryCommunityOwnerAccount, importOwnerPrestoreAccount } from '@/api/account/accountManageApi'
import DeleteAccount from '@/components/account/deleteAccount'
import PrestoreAccount from '@/components/account/prestoreAccount'
import { getDict } from '@/api/community/communityApi'
import * as XLSX from 'xlsx'

const toText = value => {
  if (value === null || value === undefined || value === '') return ''
  if (typeof value === 'number') {
    if (Number.isInteger(value) || Math.abs(value - Math.round(value)) < 1e-6) {
      return String(Math.round(value))
    }
    return String(value)
  }
  return String(value).trim()
}

const buildImportFields = (needRoom) => {
  const fields = [
    {
      key: 'ownerName',
      label: '业主名称',
      required: true,
      aliases: ['业主', '账户名称', '客户名称'],
      transform: toText,
      ruleItems: ['须与系统业主名称完全一致', '示例：xxx']
    },
    {
      key: 'ownerTel',
      label: '手机号',
      required: false,
      aliases: ['联系电话', '联系方式', '电话'],
      transform: toText,
      ruleItems: ['有号时须与系统业主手机号一致', '无号可空，须姓名在本小区户主中唯一', '示例：xxx']
    }
  ]
  if (needRoom) {
    fields.push({
      key: 'roomName',
      label: '扣款房号',
      required: true,
      aliases: ['房屋信息', '房屋', '房号', '房间'],
      transform: toText,
      ruleItems: ['须与系统房屋编号一致，如 1栋-1-1-1', '物业费/水电费扣款账户按房屋建账']
    })
  }
  fields.push(
    {
      key: 'amount',
      label: '预存金额',
      required: true,
      aliases: ['账户金额', '金额', '余额'],
      transform: toText,
      ruleItems: ['填正式系统里该类账户的当前余额，导入后覆盖成这个数', '可填 0；已等于目标则跳过']
    },
    {
      key: 'remark',
      label: '备注',
      required: false,
      aliases: ['说明'],
      transform: toText,
      ruleItems: ['可空']
    }
  )
  return fields
}

export default {
  name: 'AccountManageList',
  components: {
    DeleteAccount,
    PrestoreAccount
  },
  data() {
    return {
      loading: false,
      accounts: [],
      accountTypes: [],
      importErrorVisible: false,
      importErrorSummary: '',
      importErrors: [],
      exporting: false,
      conditions: {
        acctType: '',
        acctName: '',
        idCard: '',
        link: '',
        page: 1,
        row: 10
      },
      pagination: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.loadAccountTypes()
    this.fetchAccounts()
  },
  computed: {
    importAcctTypeName() {
      const hit = (this.accountTypes || []).find(item => item.statusCd === this.conditions.acctType)
      return hit ? hit.name : ''
    },
    needRoom() {
      return this.conditions.acctType === '2004' || this.conditions.acctType === '2005'
    },
    importFields() {
      return buildImportFields(this.needRoom)
    }
  },
  methods: {
    async loadAccountTypes() {
      try {
        const data = await getDict('account', 'acct_type')
        this.accountTypes = [{ name: this.$t('common.all'), statusCd: '' }, ...data]
      } catch (error) {
        console.error('Failed to load account types:', error)
      }
    },

    async fetchAccounts() {
      this.loading = true
      try {
        const res = await queryCommunityOwnerAccount(this.conditions)
        this.accounts = res.data || []
        this.pagination.total = res.total || 0
        this.pagination.size = this.conditions.row
      } catch (error) {
        this.$message.error(this.$t('common.fetchError'))
      } finally {
        this.loading = false
      }
    },

    queryAccounts() {
      this.conditions.page = 1
      this.fetchAccounts()
    },

    resetConditions() {
      this.conditions = {
        ...this.conditions,
        acctName: '',
        idCard: '',
        link: '',
        acctType: '',
        page: 1
      }
      this.fetchAccounts()
    },

    switchAccountType(item) {
      this.conditions.acctType = item.statusCd
      this.queryAccounts()
    },

    openDeleteDialog(account) {
      this.$refs.deleteDialog.open(account)
    },

    viewAccountDetail(account) {
      this.$router.push(`/views/account/accountDetailManage?acctId=${account.acctId}`)
    },

    openPrestore() {
      this.$refs.prestoreAccount.open({})
    },

    openImport() {
      if (!this.conditions.acctType) {
        this.$message.warning('请先在左侧选择账户类型。选「全部」时无法确定导入到哪一类账户')
        return
      }
      const acctName = this.importAcctTypeName || '账户'
      this.$confirm(
        '<div style="font-size:16px;line-height:1.8;padding-top:4px">' +
          '<div style="color:#F56C6C;font-size:22px;font-weight:700;letter-spacing:1px">覆盖导入，不是累加</div>' +
          '<div style="margin-top:12px">将按 Excel 把「' + acctName + '」写成<strong>正式系统当前余额</strong>。</div>' +
          '<div style="margin-top:8px">以<strong>本次导入的数据为准</strong>：表里是多少，系统就改成多少。</div>' +
          '<div style="color:#F56C6C;margin-top:12px;font-weight:700">已有余额会被覆盖。对不上的户整批不写。</div>' +
        '</div>',
        '覆盖导入确认',
        {
          type: 'warning',
          dangerouslyUseHTMLString: true,
          distinguishCancelAndClose: true,
          closeOnClickModal: false,
          confirmButtonText: '我已了解，继续导入',
          cancelButtonText: '取消',
          confirmButtonClass: 'el-button--danger'
        }
      ).then(() => {
        const mapper = this.$refs.prestoreImportMapper
        if (mapper && typeof mapper.open === 'function') mapper.open()
      }).catch(() => {})
    },

    async exportAccounts() {
      if (!this.conditions.acctType) {
        this.$message.warning('请先在左侧选择账户类型。选「全部」时无法确定导出哪一类账户')
        return
      }
      this.exporting = true
      try {
        const pageSize = 2000
        let page = 1
        let total = 0
        const list = []
        do {
          const res = await queryCommunityOwnerAccount({
            acctType: this.conditions.acctType,
            page,
            row: pageSize
          })
          total = Number(res.total || 0)
          const chunk = res.data || []
          list.push(...chunk)
          if (!chunk.length) break
          page += 1
        } while (list.length < total && page < 50)

        if (!list.length) {
          this.$message.warning('当前没有可导出的账户')
          return
        }

        const header = this.needRoom
          ? ['业主名称', '手机号', '扣款房号', '预存金额', '备注']
          : ['业主名称', '手机号', '预存金额', '备注']
        const rows = list.map(item => {
          const name = toText(item.acctName)
          const tel = toText(item.link)
          const amount = toText(item.amount)
          const room = toText(item.roomName)
          return this.needRoom
            ? [name, tel, room, amount, room]
            : [name, tel, amount, room]
        })
        const ws = XLSX.utils.aoa_to_sheet([header, ...rows])
        ws['!cols'] = this.needRoom
          ? [{ wch: 16 }, { wch: 14 }, { wch: 16 }, { wch: 12 }, { wch: 18 }]
          : [{ wch: 16 }, { wch: 14 }, { wch: 12 }, { wch: 18 }]
        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, '预存数据')
        const fileName = (this.importAcctTypeName || '账户') + '余额导出.xlsx'
        XLSX.writeFile(wb, fileName)
        this.$message.success('已导出 ' + rows.length + ' 户「' + (this.importAcctTypeName || '账户') + '」当前余额')
      } catch (error) {
        this.$message.error((error && error.message) || '导出失败')
      } finally {
        this.exporting = false
      }
    },

    downloadTemplate() {
      const header = this.needRoom
        ? ['业主名称', '手机号', '扣款房号', '预存金额', '备注']
        : ['业主名称', '手机号', '预存金额', '备注']
      const sample = this.needRoom
        ? ['xxx', 'xxx', '1栋-1-1-1', '100.00', '正式系统余额']
        : ['xxx', 'xxx', '100.00', '正式系统余额']
      const ws = XLSX.utils.aoa_to_sheet([header, sample])
      ws['!cols'] = this.needRoom
        ? [{ wch: 16 }, { wch: 14 }, { wch: 16 }, { wch: 12 }, { wch: 18 }]
        : [{ wch: 16 }, { wch: 14 }, { wch: 12 }, { wch: 18 }]
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, '预存数据')
      XLSX.writeFile(wb, (this.importAcctTypeName || '账户') + '余额导入模板.xlsx')
    },

    async submitPrestoreImport(formData, context) {
      const acctType = this.conditions.acctType
      if (!acctType) {
        const err = new Error('请先在左侧选择账户类型')
        err.data = []
        throw err
      }
      const rows = (context.rows || []).map((row, index) => ({
        rowIndex: index + 1,
        ownerName: String(row.ownerName || '').trim(),
        ownerTel: String(row.ownerTel || '').trim(),
        roomName: String(row.roomName || '').trim(),
        amount: String(row.amount || '').trim(),
        remark: String(row.remark || '').trim()
      }))
      if (!rows.length) {
        const err = new Error(this.$t('accountManage.importEmpty'))
        err.data = []
        throw err
      }
      const response = await importOwnerPrestoreAccount({ acctType, rows })
      if (response && Number(response.code) !== 0) {
        const errors = Array.isArray(response.data) ? response.data : []
        this.showImportErrors(response.msg || this.$t('accountManage.importFailed'), errors)
        const err = new Error(response.msg || this.$t('accountManage.importFailed'))
        err.data = errors
        throw err
      }
      return response
    },

    showImportErrors(summary, errors) {
      this.importErrorSummary = summary || this.$t('accountManage.importFailed')
      this.importErrors = (errors || []).map(item => ({
        rowIndex: item.rowIndex || '-',
        ownerName: item.ownerName || '',
        ownerTel: item.ownerTel || '',
        message: item.message || item.msg || ''
      }))
      this.importErrorVisible = true
    },

    handleImportError(error) {
      const errors = error && error.data
      if (Array.isArray(errors) && errors.length) {
        this.showImportErrors(error.message || error.msg || this.$t('accountManage.importFailed'), errors)
      }
    },

    handleImportSuccess(response) {
      const data = (response && response.data) || {}
      const changed = data.successCount || 0
      const skipped = data.skipCount || 0
      this.$message({
        type: 'success',
        message: '已按正式余额覆盖 ' + changed + ' 户「' + (this.importAcctTypeName || '账户') + '」' + (skipped ? '，' + skipped + ' 户已相同已跳过' : ''),
        duration: 0,
        showClose: true
      })
      this.fetchAccounts()
    },

    handleSizeChange(size) {
      this.conditions.row = size
      this.fetchAccounts()
    },

    handlePageChange(page) {
      this.conditions.page = page
      this.fetchAccounts()
    }
  }
}
</script>

<style scoped>
.account-manage-container {
  padding: 20px;
}

.tree-card {
  height: 100%;
}

.account-type-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.account-type-item {
  padding: 12px 15px;
  margin-bottom: 8px;
  border-radius: 4px;
  cursor: pointer;
  text-align: center;
  transition: all 0.3s;
}

.account-type-item:hover {
  background-color: #f5f7fa;
}

.account-type-item.active {
  background-color: #409eff;
  color: white;
  border-color: #409eff;
}

.search-card {
  margin-bottom: 20px;
}

.flex-between {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.mt-20 {
  margin-top: 20px;
}
</style>